import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, MenuItem, Typography } from "@mui/material";

import { PostDeleteIcon, PostEditIcon, PostReportIcon, PostUnfollowIcon } from "../../../assets/icons/create-feed";
import useIsOwn from "../../../utils/useIsOwn";
import { usePostDeleteMutation } from "../../../api/mutations/usePostDeleteMutation";
import { usePostsReportReasons } from "../../../api/queries/postsReportReasons.query";

import { PostReportModal } from "../PostReportModal";
import { PostDeleteModal, usePostDeleteModal } from "./PostDeleteModal";

interface PostCardMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  authorFullName: string;
  authorId: number;
  postId: number;
}

const menuItemStyles = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const menuItemTypographyStyles = {
  color: "var(--Dark---1000, #111)",
  fontFamily: "Montserrat",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "20px",
};

const PostMenuItem = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => {
  return (
    <MenuItem sx={menuItemStyles} onClick={onClick}>
      {icon}
      <Typography sx={menuItemTypographyStyles}>{label}</Typography>
    </MenuItem>
  );
};

const PostCardMenu = ({ anchorEl, onClose, authorFullName, authorId, postId }: PostCardMenuProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "postHeader" });
  const { deletePost, isDeleting } = usePostDeleteMutation({ postId });
  const { data: reportReasons = [] } = usePostsReportReasons();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const { isOwn, isLoading: isOwnLoading } = useIsOwn({ authorId, isAuthorIdLoading: false });

  const handleOpenReportModal = () => {
    onClose();
    setIsReportModalOpen(true);
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
  };

  const normalPostMenuItems = [
    {
      icon: <PostReportIcon />,
      label: t("report"),
      onClick: handleOpenReportModal,
    },
    {
      icon: <PostUnfollowIcon />,
      label: t("unfollow", { name: authorFullName }),
      onClick: () => {
        onClose();
      },
    },
  ];

  const {
    isOpen: isDeleteModalOpen,
    handleOpen: handleOpenDeleteModal,
    handleClose: handleCloseDeleteModal,
    postId: deletePostId,
  } = usePostDeleteModal();

  const ownPostMenuItems = [
    // {
    //   icon: <PostEditIcon />,
    //   label: t("edit"),
    //   onClick: () => {
    //     onClose();
    //     navigate(getEditPostRoute(postId));
    //   },
    // },
    {
      icon: <PostDeleteIcon />,
      label: t("delete"),
      onClick: () => {
        // deletePost();
        handleOpenDeleteModal(postId);
        onClose();
      },
    },
  ];

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorEl)}
        onClose={onClose}
      >
        {!isOwnLoading &&
          isOwn &&
          ownPostMenuItems.map(({ icon, label, onClick }, index) => (
            <PostMenuItem key={index} icon={icon} label={label} onClick={onClick} />
          ))}
        {!isOwnLoading &&
          !isOwn &&
          normalPostMenuItems.map(({ icon, label, onClick }, index) => (
            <PostMenuItem key={index} icon={icon} label={label} onClick={onClick} />
          ))}
      </Menu>

      <PostReportModal
        open={isReportModalOpen}
        onClose={handleCloseReportModal}
        postId={postId}
        reportReasons={reportReasons}
      />
      <PostDeleteModal open={isDeleteModalOpen} onClose={handleCloseDeleteModal} postId={deletePostId} />
    </>
  );
};

export default PostCardMenu;
